import React from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';

type XeroIntegrationConnectProps = {
    isConnected: boolean;
    isEnabled: boolean;
    isSaving: boolean;
    connectionLabel?: string | null;
    autoDisabledMessage?: string | null;
    onConnect: () => void;
    onToggleEnabled: (checked: boolean) => void;
};

export default function XeroIntegrationConnect({
    isConnected,
    isEnabled,
    isSaving,
    connectionLabel,
    autoDisabledMessage,
    onConnect,
    onToggleEnabled
}: XeroIntegrationConnectProps) {
    return (
        <Box sx={{ width: '100%' }}>
            {autoDisabledMessage && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography>Integration was automatically disabled:</Typography>
                    <Typography sx={{ fontWeight: 'bold', my: 1 }}>{autoDisabledMessage}</Typography>
                    <Typography>Enable the integration again after resolving the issue.</Typography>
                </Alert>
            )}

            <Alert severity={isConnected ? 'success' : 'info'} sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    {isConnected ? 'Xero is connected' : 'Xero is not connected yet'}
                </Typography>
                <Typography>
                    {isConnected
                        ? `Connected tenant: ${connectionLabel || 'Unknown tenant'}`
                        : 'Authorize AbakusIQ against Xero using OAuth 2.0 to start syncing customers, products and invoices.'}
                </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <Button variant="contained" onClick={onConnect} disabled={isSaving}>
                    {isConnected ? 'Reconnect to Xero' : 'Connect to Xero'}
                </Button>
                {isSaving && <CircularProgress size={20} />}
            </Box>

            {isConnected && (
                <FormControlLabel
                    control={
                        <Switch
                            checked={isEnabled}
                            onChange={(event) => onToggleEnabled(event.target.checked)}
                            disabled={isSaving}
                        />
                    }
                    label="Enabled"
                />
            )}
        </Box>
    );
}
